import React, { FC, useCallback, useEffect } from 'react';
import { ExaminationApiContextProvider } from '@higo/common/lib/features/ExaminationPage/providers';
import { useExaminationPageModel } from '@higo/common/lib/features/ExaminationPage/hooks';
import { isNil } from 'rambda';
import {
  ExaminationPageModel,
  ExaminationPageSectionId,
  mapToExaminationPageDiagnosisFormValue,
} from '@higo/common/lib/features/ExaminationPage/models';
import { coerceNumber } from '@higo/common/lib/utils';
import styled from 'styled-components';
import { media } from '@higo/ui/lib/theme';
import { useParams } from 'react-router-dom';
import { useCreateJWTAuthorizedAxios } from 'hooks/useCreateJWTAuthorizedAxios';
import { useExchangeSharingTokenForJWTMutation } from 'pages/Shared/SharedExaminationPage/hooks/useExchangeSharingTokenForJWTMutation';
import { SharedExaminationPageSkeleton } from 'pages/Shared/SharedExaminationPage/components/SharedExaminationPageSkeleton';
import { useSelectJWTExaminationId } from 'hooks/useSelectJWTExaminationId';
import {
  AbdominalAuscultationSection,
  AnamnesisSection,
  CoughAuscultationSection,
  EarsExaminationSection,
  ExaminationPageContextSection,
  HeartAuscultationSection,
  LungsAuscultationSection,
  SkinExaminationSection,
  ThroatExaminationSection,
  ExaminationPageSummarySection,
} from '@higo/common/lib/features/ExaminationPage/sections';
import {
  ExaminationPageCovidModal,
  ExaminationPageGeneralSurveyModal,
  ExaminationPageHeader,
  ExaminationPageInfoBar,
  ExaminationPageInfoBarOuterContainer,
  ExaminationPageOverview,
  ExaminationPageTocContainer,
} from '@higo/common/lib/features/ExaminationPage/components';
import {
  Button,
  HorizontalDivider,
  LoaderIcon,
  LogoIcon,
  SurveyIcon,
} from '@higo/ui';
import { VitalSignsSection } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection';
import { LayoutContextProvider } from '@higo/ui/lib/contexts';
import { SharedExaminationPageLayout } from 'pages/Shared/SharedExaminationPage/components/SharedExaminationPageLayout';
import { FormProvider, useForm } from 'react-hook-form';
import { ExaminationDoctorDiagnosisDTO } from '@higo/api/lib/models/examinationDoctorDiagnosisDTO';
import { useExaminationDiagnosisQuery } from '@higo/common/lib/features/ExaminationPage/hooks/queries';
import { FormattedMessage } from 'react-intl';
import { FullPageMessage } from 'components/FullPageMessage';
import { useDispatchMicrositeTrackedEvent } from 'hooks/useDispatchMicrositeTrackedEvent';
import {
  DoctorTrackedType,
  TrackedEventButtonClickAction,
} from '@higo/common/lib/features/TrackedEvent';
import useMedia from 'react-use/lib/useMedia';
import { isMobileQuery } from '@higo/ui';

export const sharedExaminationPageUrlParams = {
  sharingToken: 'sharingToken',
};

const ContentContainer = styled('main')`
  display: grid;

  section {
    scroll-margin-top: 220px;

    ${media.desktop.min} {
      scroll-margin-top: 193px;
    }
  }

  ${media.desktop.min} {
    grid-template-columns: minmax(0, 1fr) minmax(2rem, 6.25rem) 20.5rem;
  }

  ${media.mobile.max}, ${media.tablet.max} {
    grid-template-columns: 1fr;
  }

  padding-bottom: 14.425rem; // allows for proper floating window position
`;

const ContextSectionWrapper = styled.div<{ bothFiles: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.bothFiles === true
      ? '2fr minmax(1rem, 6.25rem) 1fr'
      : '2fr minmax(1rem, 3rem) 3.5fr'};

  ${media.mobile.max} {
    grid-template-columns: 1fr;
  }
`;

const SharedExaminationPageContent: FC<{
  model: ExaminationPageModel;
  diagnosis?: ExaminationDoctorDiagnosisDTO;
}> = ({ model, diagnosis }) => {
  const sendTrackedEvent = useDispatchMicrositeTrackedEvent();
  const isMobileView = useMedia(`${isMobileQuery}`);

  const methods = useForm({
    defaultValues: mapToExaminationPageDiagnosisFormValue(diagnosis),
  });
  //todo: fix deps issue
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isSectionAvailable = useCallback(
    (id: ExaminationPageSectionId) =>
      model.availableExaminationPageSections.has(id),
    [model],
  );

  const hasContextFile =
    !isNil(model.details.examination.contextPhotoFileId) ||
    !isNil(model.details.examination.contextVideoFileId);

  const hasBothContextFiles =
    !isNil(model.details.examination.contextPhotoFileId) &&
    !isNil(model.details.examination.contextVideoFileId);

  return (
    <>
      {model.answeredGeneralSurvey && (
        <ExaminationPageCovidModal
          answeredGeneralSurvey={model.answeredGeneralSurvey}
        />
      )}
      <ExaminationPageHeader>
        <SharedExaminationPageLayout>
          <ExaminationPageOverview
            mode={model.mode}
            patientOverview={model.patientOverview}
            examinationId={model.details.examination.id}
            patient={model.details.patient}
            generalSurveySlot={
              model.answeredGeneralSurvey && (
                <ExaminationPageGeneralSurveyModal
                  answeredGeneralSurvey={model.answeredGeneralSurvey}
                  hasCopyFeature={false}
                  hasToastWhenCopy={false}
                  trigger={
                    <Button
                      color="secondary"
                      size="small"
                      endIcon={<SurveyIcon />}
                      style={
                        isMobileView
                          ? { width: '70%', margin: '0.4rem 0.4rem 0 0.4rem' }
                          : { width: '13.5rem' }
                      }
                      onClick={() =>
                        sendTrackedEvent({
                          event: DoctorTrackedType.ButtonClick,
                          eventAction:
                            TrackedEventButtonClickAction.OpenGeneralSurveyModal,
                          patientId: model.details.patient.id,
                          examinationId: model.details.examination.id,
                        })
                      }
                    >
                      <FormattedMessage id="generalSurvey.modalTrigger.title" />
                    </Button>
                  }
                />
              )
            }
          />
        </SharedExaminationPageLayout>
        <ExaminationPageInfoBarOuterContainer>
          <SharedExaminationPageLayout>
            <ExaminationPageInfoBar
              examinationCreateDate={
                model.details.examination.sessionEndTime ??
                model.details.examination.createDate
              }
              higoMode={model.details.patient.type} // todo: mode shouldn't be stored on user but  ¯\_(ツ)_/¯
              executedBy={model.details.executedBy}
              hasOperatorData={false}
            />
          </SharedExaminationPageLayout>
        </ExaminationPageInfoBarOuterContainer>
      </ExaminationPageHeader>
      <SharedExaminationPageLayout>
        <FormProvider {...methods}>
          <ContentContainer>
            <div>
              {model.mode === 'PRO' && (
                <VitalSignsSection
                  examinationId={model.details.examination.id}
                  physicalExaminationId={
                    model.details.examination.physicalExaminationId
                  }
                  isPhysicalExaminationCompletedFn={
                    model.isPhysicalExaminationCompletedFn
                  }
                />
              )}
              {model.mode === 'FAMILY' && (
                <>
                  <HorizontalDivider />
                  {hasContextFile ? (
                    <ContextSectionWrapper bothFiles={hasBothContextFiles}>
                      <ExaminationPageContextSection
                        contextPhotoFileId={
                          model.details.examination.contextPhotoFileId
                        }
                        contextVideoFileId={
                          model.details.examination.contextVideoFileId
                        }
                        hasBothContextFiles={hasBothContextFiles}
                      />
                      <div />
                      <VitalSignsSection
                        examinationId={model.details.examination.id}
                        physicalExaminationId={
                          model.details.examination.physicalExaminationId
                        }
                        isPhysicalExaminationCompletedFn={
                          model.isPhysicalExaminationCompletedFn
                        }
                      />
                    </ContextSectionWrapper>
                  ) : (
                    <VitalSignsSection
                      examinationId={model.details.examination.id}
                      physicalExaminationId={
                        model.details.examination.physicalExaminationId
                      }
                      isPhysicalExaminationCompletedFn={
                        model.isPhysicalExaminationCompletedFn
                      }
                    />
                  )}
                </>
              )}

              {isSectionAvailable(ExaminationPageSectionId.AnamnesisSurvey) && (
                <AnamnesisSection
                  examinationId={model.details.examination.id}
                  mode={model.mode}
                  isNotesCreateAndReadPermission={false}
                  isNotesReadPermissionOnly={true}
                  anamnesisTaggedQuestions={
                    model.answeredAnamnesisSurvey?.taggedQuestions
                  }
                  generalTaggedQuestions={
                    model.answeredGeneralSurvey?.taggedQuestions
                  }
                  hasCopyFeature={false}
                />
              )}

              {isSectionAvailable(ExaminationPageSectionId.Skin) && (
                <SkinExaminationSection
                  examinationId={model.details.examination.id}
                />
              )}

              {isSectionAvailable(ExaminationPageSectionId.Heart) && (
                <HeartAuscultationSection
                  examinationId={model.details.examination.id}
                  patientModel={model.patient}
                  diagnosisModel={model.diagnosis}
                />
              )}

              {isSectionAvailable(ExaminationPageSectionId.Lungs) && (
                <LungsAuscultationSection
                  examinationId={model.details.examination.id}
                  patientModel={model.patient}
                  diagnosisModel={model.diagnosis}
                />
              )}

              {isSectionAvailable(ExaminationPageSectionId.Cough) && (
                <CoughAuscultationSection
                  examinationId={model.details.examination.id}
                />
              )}

              {isSectionAvailable(ExaminationPageSectionId.Ears) && (
                <EarsExaminationSection
                  examinationId={model.details.examination.id}
                  isPhysicalExaminationCompletedFn={
                    model.isPhysicalExaminationCompletedFn
                  }
                />
              )}

              {isSectionAvailable(ExaminationPageSectionId.Throat) && (
                <ThroatExaminationSection
                  examinationId={model.details.examination.id}
                />
              )}

              {isSectionAvailable(ExaminationPageSectionId.Abdominal) && (
                <AbdominalAuscultationSection
                  examinationId={model.details.examination.id}
                />
              )}

              <ExaminationPageSummarySection
                patientOverview={model.patientOverview}
                isReadOnly={true}
              />
            </div>
          </ContentContainer>
        </FormProvider>
      </SharedExaminationPageLayout>
    </>
  );
};

const SharedExaminationPageDataLoader: FC<{ id: number }> = ({ id }) => {
  const examId = coerceNumber(id);
  const model = useExaminationPageModel(examId);
  const { data: diagnosisData, isLoading: isDiagnosisLoading } =
    useExaminationDiagnosisQuery(
      examId,
      model?.isDiagnosisAvailable ?? false,
      false,
    );

  return isNil(model) || isDiagnosisLoading ? (
    <SharedExaminationPageSkeleton />
  ) : (
    <SharedExaminationPageContent model={model} diagnosis={diagnosisData} />
  );
};

const SharedExaminationPage = () => {
  const { [sharedExaminationPageUrlParams.sharingToken]: sharingToken } =
    useParams();
  const {
    isLoading,
    data: jwtToken,
    mutate,
  } = useExchangeSharingTokenForJWTMutation();

  const examinationId = useSelectJWTExaminationId(jwtToken?.access_token);

  useEffect(() => {
    sharingToken && mutate(sharingToken);
  }, [sharingToken, mutate]);

  const authorizedAxios = useCreateJWTAuthorizedAxios(jwtToken?.access_token);

  if (isLoading) {
    return (
      <FullPageMessage icon={<LoaderIcon />}>
        <FormattedMessage id="microsite.loadingExamination" />
      </FullPageMessage>
    );
  }

  if (!examinationId || !authorizedAxios) {
    return (
      <FullPageMessage icon={<LogoIcon />}>
        <FormattedMessage id="microsite.invalidTokenProvided" />
      </FullPageMessage>
    );
  } else {
    return (
      <LayoutContextProvider>
        <ExaminationApiContextProvider value={authorizedAxios}>
          <ExaminationPageTocContainer>
            <SharedExaminationPageDataLoader id={examinationId} />
          </ExaminationPageTocContainer>
        </ExaminationApiContextProvider>
      </LayoutContextProvider>
    );
  }
};

export default SharedExaminationPage;
